import React, { createContext, useContext, ReactNode, useMemo } from 'react';

interface UsageContextValue {
  usage: number;
  limit: number;
  remaining: number;
  isCapped: boolean;
  documentChat?: {
    usage: number;
    limit: number;
    remaining: number;
    isCapped: boolean;
  };
  refresh: () => Promise<void> | void;
}

const UsageContext = createContext<UsageContextValue | undefined>(undefined);

interface UsageProviderProps {
  usage?: number | null;
  limit?: number | null;
  documentChat?: { usage: number; limit: number } | null;
  refresh: () => Promise<void> | void;
  children: ReactNode;
}

export const UsageProvider: React.FC<UsageProviderProps> = ({ usage, limit, documentChat, refresh, children }) => {
  const value = useMemo<UsageContextValue>(() => {
    const u = typeof usage === 'number' ? usage : 0;
    const l = typeof limit === 'number' ? limit : 50;
    const remaining = Math.max(0, l - u);
    const isCapped = u >= l;

    const docChat = documentChat ? {
      usage: documentChat.usage,
      limit: documentChat.limit,
      remaining: Math.max(0, documentChat.limit - documentChat.usage),
      isCapped: documentChat.usage >= documentChat.limit
    } : undefined;

    return { usage: u, limit: l, remaining, isCapped, documentChat: docChat, refresh };
  }, [usage, limit, documentChat, refresh]);

  return (
    <UsageContext.Provider value={value}>
      {children}
    </UsageContext.Provider>
  );
};

export function useUsage(): UsageContextValue {
  const ctx = useContext(UsageContext);
  if (!ctx) {
    // Provide a safe default to avoid crashes if not wrapped; no-op refresh
    return { usage: 0, limit: 50, remaining: 50, isCapped: false, refresh: () => {} };
  }
  return ctx;
}



